/**
 * Material-UI Theme Factory
 *
 * Transforms design tokens into a Material-UI theme configuration.
 * This factory creates a complete MUI theme object from our theme tokens.
 */

import { createTheme } from '@mui/material/styles';

/**
 * Create Material-UI theme from design tokens
 * @param {object} themeTokens - Design tokens from themeAdapter
 * @returns {object} Material-UI theme object
 */
export const createMuiTheme = (themeTokens = {}) => {
  const {
    colors = {},
    typography = {},
    spacing = {},
    customCSS = '',
  } = themeTokens;

  // Create MUI theme configuration
  const muiTheme = createTheme({
    // ========================================================================
    // PALETTE
    // ========================================================================
    palette: {
      mode: themeTokens.mode || 'light', // Support dark mode from theme tokens
      primary: {
        main: colors.primary?.main || '#2A60FF', // Bright blue - Template Overview card
        light: colors.primary?.light || '#5A80FF', // Lighter blue
        dark: colors.primary?.dark || '#1A40CC', // Darker blue
        contrastText: colors.primary?.contrastText || '#ffffff',
      },

      secondary: {
        main: colors.secondary?.main || '#2563EB', // Modern blue - Avg Execution Time card
        light: colors.secondary?.light || '#3B82F6', // Lighter blue
        dark: colors.secondary?.dark || '#1D4ED8', // Darker blue
        contrastText: colors.secondary?.contrastText || '#ffffff',
      },

      error: {
        // Use consistent red color for errors - never purple
        main: colors.error?.main || '#EF4444', // Modern red
        light: colors.error?.light || '#F87171', // Lighter red
        dark: colors.error?.dark || '#DC2626', // Darker red
        contrastText: colors.error?.contrastText || '#ffffff',
      },

      warning: {
        main: colors.warning?.main || '#FFA02A', // Bright orange - Total Cost card
        light: colors.warning?.light || '#FFB855', // Lighter orange
        dark: colors.warning?.dark || '#CC8000', // Darker orange
        contrastText: colors.warning?.contrastText || '#ffffff',
      },

      info: {
        main: colors.info?.main || '#2AC0C4', // Teal/Cyan - Success Rate card
        light: colors.info?.light || '#4AE0E4', // Lighter teal
        dark: colors.info?.dark || '#1A9094', // Darker teal
        contrastText: colors.info?.contrastText || '#ffffff',
      },

      success: {
        main: colors.success?.main || '#2AC460', // Vibrant green - Completed Executions card
        light: colors.success?.light || '#4AE480', // Lighter green
        dark: colors.success?.dark || '#1A9440', // Darker green
        contrastText: colors.success?.contrastText || '#ffffff',
      },

      background: {
        default: colors.background?.default || '#ffffff',
        paper: colors.background?.paper || '#f0f8ff',
      },

      text: {
        primary: colors.text?.primary || '#000000',
        secondary: colors.text?.secondary || 'rgba(0, 0, 0, 0.7)',
        disabled: colors.text?.disabled || 'rgba(0, 0, 0, 0.38)',
      },

      divider: colors.divider || 'rgba(0, 0, 0, 0.12)',

      // Custom accent color (accessible via theme.palette.accent)
      accent: {
        main: colors.accent?.main || '#28a745',
        light: colors.accent?.light || '#51c36b',
        dark: colors.accent?.dark || '#1c7430',
        contrastText: colors.accent?.contrastText || '#ffffff',
      },
    },

    // ========================================================================
    // TYPOGRAPHY
    // ========================================================================
    typography: {
      fontFamily:
        typography.fontFamily ||
        "'Inter', 'Roboto', 'Helvetica Neue', 'Arial', sans-serif",
      fontSize: typography.fontSize || 14,
      fontWeightLight: typography.fontWeightLight || 300,
      fontWeightRegular: typography.fontWeightRegular || 400,
      fontWeightMedium: typography.fontWeightMedium || 500,
      fontWeightBold: typography.fontWeightBold || 700,

      h1: {
        fontSize: typography.h1?.fontSize || '2.5rem',
        fontWeight: typography.h1?.fontWeight || 700,
        lineHeight: typography.h1?.lineHeight || 1.2,
      },
      h2: {
        fontSize: typography.h2?.fontSize || '2rem',
        fontWeight: typography.h2?.fontWeight || 700,
        lineHeight: typography.h2?.lineHeight || 1.3,
      },
      h3: {
        fontSize: typography.h3?.fontSize || '1.75rem',
        fontWeight: typography.h3?.fontWeight || 700,
        lineHeight: typography.h3?.lineHeight || 1.3,
      },
      h4: {
        fontSize: typography.h4?.fontSize || '1.5rem',
        fontWeight: typography.h4?.fontWeight || 700,
        lineHeight: typography.h4?.lineHeight || 1.4,
      },
      h5: {
        fontSize: typography.h5?.fontSize || '1.25rem',
        fontWeight: typography.h5?.fontWeight || 600,
        lineHeight: typography.h5?.lineHeight || 1.4,
      },
      h6: {
        fontSize: typography.h6?.fontSize || '1.125rem',
        fontWeight: typography.h6?.fontWeight || 600,
        lineHeight: typography.h6?.lineHeight || 1.4,
      },

      body1: {
        fontSize: typography.body1?.fontSize || '1rem',
        fontWeight: typography.body1?.fontWeight || 400,
        lineHeight: typography.body1?.lineHeight || 1.5,
      },
      body2: {
        fontSize: typography.body2?.fontSize || '0.875rem',
        fontWeight: typography.body2?.fontWeight || 400,
        lineHeight: typography.body2?.lineHeight || 1.5,
      },

      button: {
        textTransform: 'none', // Disable uppercase transform
        fontWeight: 600,
      },

      caption: {
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: 1.4,
      },

      overline: {
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 1.4,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      },
    },

    // ========================================================================
    // SPACING
    // ========================================================================
    spacing: spacing.unit || 8,

    // ========================================================================
    // SHAPE
    // ========================================================================
    shape: {
      borderRadius: spacing.borderRadius || 8,
    },

    // ========================================================================
    // SHADOWS
    // ========================================================================
    // Use default MUI shadows, could be customized based on theme

    // ========================================================================
    // COMPONENTS
    // ========================================================================
    components: {
      // Button overrides
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: spacing.borderRadius || 8,
            fontWeight: 600,
            minWidth: 80,
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            // Allow fullWidth buttons to override maxWidth
            '&.MuiButton-fullWidth': {
              maxWidth: 'none',
            },
          },
          sizeSmall: {
            minWidth: 64,
            maxWidth: 150,
          },
          sizeLarge: {
            minWidth: 100,
            maxWidth: 250,
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
        },
      },

      // Card overrides
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: (spacing.borderRadius || 8) * 1.5,
          },
        },
      },

      // Paper overrides
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: spacing.borderRadius || 8,
          },
        },
      },

      // TextField overrides
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: spacing.borderRadius || 8,
            },
          },
        },
      },

      // Select overrides
      MuiSelect: {
        styleOverrides: {
          root: {
            minWidth: 120,
            maxWidth: 300,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
      },

      // FormControl overrides
      MuiFormControl: {
        styleOverrides: {
          root: {
            '& .MuiSelect-root': {
              minWidth: 120,
              maxWidth: 300,
            },
          },
        },
      },

      // InputLabel overrides
      MuiInputLabel: {
        styleOverrides: {
          root: {
            minWidth: 60,
            maxWidth: 150,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        },
      },

      // Chip overrides
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: (spacing.borderRadius || 8) * 2,
            minWidth: 40,
            maxWidth: 120,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          sizeSmall: {
            minWidth: 40,
            maxWidth: 100,
            height: 24,
          },
        },
      },

      // MenuItem overrides
      MuiMenuItem: {
        styleOverrides: {
          root: {
            minWidth: 120,
            maxWidth: 300,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
      },

      // CssBaseline for global styles and custom CSS
      MuiCssBaseline: {
        styleOverrides: customCSS || '',
      },
    },

    // ========================================================================
    // TRANSITIONS
    // ========================================================================
    transitions: {
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
    },
  });

  return muiTheme;
};

/**
 * Create a theme variant (light/dark mode toggle)
 * @param {object} baseTheme - Base MUI theme
 * @param {string} mode - Theme mode ('light' or 'dark')
 * @returns {object} Theme variant
 */
export const createThemeVariant = (baseTheme, mode = 'light') => {
  return createTheme({
    ...baseTheme,
    palette: {
      ...baseTheme.palette,
      mode,
    },
  });
};

const muiThemeFactory = {
  createMuiTheme,
  createThemeVariant,
};

export default muiThemeFactory;
